<template>
  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th :style="headerStyleCorner"></th>
          <th v-for="(col, colIndex) in gridData[0]" :key="colIndex" :style="headerHStyle">
            {{ colIndex + 1 }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(row, rowIndex) in gridData" :key="rowIndex">
          <th :style="headerVStyle">
            {{ rowIndex + 1 }}
          </th>

          <td
            v-for="(cell, colIndex) in row"
            :key="colIndex"
            :style="[tdStyle, { backgroundColor: cell.color }]"
          >
            <input
              :ref="inputRefName(rowIndex, colIndex)"
              :data-ref="inputRefName(rowIndex, colIndex)"
              v-model="cell.value"
              @input="handleInput($event, rowIndex, colIndex)"
              @keydown.left.prevent="focusCellArrow(rowIndex, colIndex - 1)"
              @keydown.right.prevent="focusCellArrow(rowIndex, colIndex + 1)"
              @keydown.up.prevent="focusCellArrow(rowIndex - 1, colIndex)"
              @keydown.down.prevent="focusCellArrow(rowIndex + 1, colIndex)"
              @focus="positionCaretAtEnd"
              @click="positionCaretAtEnd"
              maxlength="1"
              :disabled="cell.color === 'Lawngreen' || isDisabled"
              class="grid-input"
            />
            <span v-if="cell.letterType && cell.color !== 'Lawngreen'" class="vc_pattern">
              {{ cell.letterType }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import type { GridData } from '../../types/grid'

interface Props {
  gridData: GridData[][]
  isDisabled?: boolean
}

interface Emits {
  (e: 'cell-input', rowIndex: number, colIndex: number): void
}

const props = withDefaults(defineProps<Props>(), {
  isDisabled: false,
})

const emit = defineEmits<Emits>()

const computedCellSize = computed(() => {
  const totalColumns = props.gridData.length
  const totalHeight = window.innerHeight
  const totalWidth = window.innerWidth

  const max_size_h = Math.floor(totalWidth / (totalColumns + 2)) // we include header
  const max_size_v = Math.floor((totalHeight * 0.45) / totalColumns)
  const cell_size = Math.min(max_size_h, max_size_v)

  return cell_size
})

const headerHStyle = computed(() => ({
  width: computedCellSize.value + 'px',
  height: '30px',
  textAlign: 'center' as const,
  fontSize: '20px',
  border: '0px',
  fontWeight: 'normal' as const,
}))

const headerStyleCorner = computed(() => ({
  width: '30px',
  height: '30px',
  textAlign: 'center' as const,
  fontSize: '20px',
  border: '0px',
  fontWeight: 'normal' as const,
}))

const headerVStyle = computed(() => ({
  width: '30px',
  height: computedCellSize.value + 'px',
  textAlign: 'center' as const,
  fontSize: '20px',
  border: '0px',
  fontWeight: 'normal' as const,
}))

const tdStyle = computed(() => ({
  width: computedCellSize.value + 'px',
  height: computedCellSize.value + 'px',
  border: '2px solid black',
  textAlign: 'center' as const,
  position: 'relative' as const,
}))

const handleInput = (event: Event, rowIndex: number, colIndex: number): void => {
  // Only allow letters as input
  const input = event.target as HTMLInputElement
  const originalValue = input.value

  // Clean the input value to only allow letters
  const cleanedValue = originalValue.replace(/[^a-zA-ZñÑáéíóúÁÉÍÓÚüÜ]/g, '')

  // If the value was changed, update it
  if (cleanedValue !== originalValue) {
    input.value = cleanedValue
    // Update the v-model manually since we changed the input value
    props.gridData[rowIndex][colIndex].value = cleanedValue
  }

  // Emit the cell-input event
  emit('cell-input', rowIndex, colIndex)
}

const inputRefName = (row: number, col: number): string => {
  return `cell-${row}-${col}`
}

const getNextAvailableCell = (row: number, col: number): [number, number] | null => {
  const size = props.gridData.length

  // Start from the current position and look for the next available cell
  for (let r = row; r < size; r++) {
    for (let c = r === row ? col + 1 : 0; c < size; c++) {
      const cell = props.gridData[r][c]
      if (cell.color !== 'Lawngreen') {
        return [r, c]
      }
    }
  }

  // If no available cell found after current position, start from beginning
  for (let r = 0; r < row; r++) {
    for (let c = 0; c < size; c++) {
      const cell = props.gridData[r][c]
      if (cell.color !== 'Lawngreen') {
        return [r, c]
      }
    }
  }

  return null
}

const focusNextAvailableCell = (row: number, col: number): void => {
  const nextCell = getNextAvailableCell(row, col)
  if (nextCell) {
    focusCell(nextCell[0], nextCell[1])
  }
}

const focusCell = (row: number, col: number): void => {
  if (row >= 0 && row < props.gridData.length && col >= 0 && col < props.gridData.length) {
    nextTick(() => {
      const inputRef = inputRefName(row, col)
      // Note: In Vue 3, we need to handle refs differently
      // This is a simplified implementation
      const input = document.querySelector(`[data-ref="${inputRef}"]`) as HTMLInputElement
      if (input) {
        input.focus()
        input.setSelectionRange(input.value.length, input.value.length)
      }
    })
  }
}

const focusCellArrow = (row: number, col: number): void => {
  if (row >= 0 && row < props.gridData.length && col >= 0 && col < props.gridData.length) {
    focusCell(row, col)
  }
}

const positionCaretAtEnd = (event: Event): void => {
  const input = event.target as HTMLInputElement
  input.setSelectionRange(input.value.length, input.value.length)
}

// Expose methods to parent component
defineExpose({
  focusNextAvailableCell,
  getNextAvailableCell,
  focusCell,
  focusCellArrow,
})
</script>

<style scoped>
.table-container {
  margin: 20px auto;
}

table {
  border-collapse: collapse;
  margin: 0 auto;
}

.grid-input {
  width: 100%;
  height: 100%;
  text-align: center;
  border: none;
  text-transform: uppercase;
  background: transparent;
  font-size: 30px;
  padding: 0px;
}

.vc_pattern {
  width: 100%;
  height: 100%;
  text-align: right;
  border: none;
  text-transform: uppercase;
  background: transparent;
  font-size: 16px;
  position: absolute;
  right: 0px;
  top: 0px;
  margin-right: 2px;
  margin-top: 2px;
  pointer-events: none;
}

@media (max-width: 1200px) {
  .table-container {
    overflow-x: auto;
  }
}
</style>
